import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-291">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-292">
        <Card data-id="element-293">
          <CardContent className="p-5" data-id="element-294">
            <div className="flex items-start gap-3" data-id="element-295">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-296">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-297" />
              </div>
              <div className="min-w-0" data-id="element-298">
                <p className="text-sm text-gray-500 font-medium" data-id="element-299">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-300">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-301">
          <CardContent className="p-5" data-id="element-302">
            <div className="flex items-start gap-3" data-id="element-303">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-304">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-305" />
              </div>
              <div className="min-w-0" data-id="element-306">
                <p className="text-sm text-gray-500 font-medium" data-id="element-307">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-308">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-309">
          <CardContent className="p-5" data-id="element-310">
            <div className="flex items-start gap-3" data-id="element-311">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-312">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-313" />
              </div>
              <div className="min-w-0" data-id="element-314">
                <p className="text-sm text-gray-500 font-medium" data-id="element-315">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-316">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-317">
        {/* Peak Day */}
        <Card data-id="element-318">
          <CardContent className="p-5" data-id="element-319">
            <div className="flex items-center gap-2 mb-3" data-id="element-320">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-321">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-322" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-323">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-324">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-325">
                    <div className="flex items-center gap-2" data-id="element-326">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-327">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-328">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-329">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-330">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-331">
          <CardContent className="p-5" data-id="element-332">
            <div className="flex items-center gap-2 mb-3" data-id="element-333">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-334">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-335" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-336">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-337">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-338">
                    <div className="flex items-center gap-2" data-id="element-339">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-340">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-341">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-342">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-343">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-344">
          <CardContent className="p-5" data-id="element-345">
            <div className="flex items-center gap-2 mb-3" data-id="element-346">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-347">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-348" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-349">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-350">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-351">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-352">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-353">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-354">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-355">
          <CardContent className="p-5" data-id="element-356">
            <div className="flex items-center gap-2 mb-3" data-id="element-357">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-358">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-359" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-360">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-361">
              <p className="text-2xl font-bold text-blue-600" data-id="element-362">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-363">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-364">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-365">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}